import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REGEXP_SPACE } from '@/constants/regex';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import { adjacencyGraphs, dictionary as commonDictionary } from '@zxcvbn-ts/language-common';
import { dictionary as enDictionary, translations } from '@zxcvbn-ts/language-en';

zxcvbnOptions.setOptions({
  dictionary: {
    ...commonDictionary,
    ...enDictionary,
  },
  graphs: adjacencyGraphs,
  translations,
});

export enum PwdStrength {
  NotSet,
  Weak,
  Fair,
  Good,
  Strong,
}

export function usePwdStrength() {
  const { t } = useTranslation();
  const [strength, setStrength] = useState(PwdStrength.NotSet);

  const cb = (s: PwdStrength, error?: string) => {
    setStrength(s);
    return error;
  };

  const validatePwd = (pwd: string) => {
    if (pwd === '' || pwd === undefined) return cb(PwdStrength.NotSet, t('new_password_required_validation'));
    else if (pwd.length < 8) return cb(PwdStrength.Weak, t('password_length_error'));
    else if (pwd.length > 20) return cb(PwdStrength.Fair, t('max_length_validation', { length: 20 }));
    else if (!REGEXP_SPACE.test(pwd)) return cb(PwdStrength.Fair, t('password_space_error'));
    // allow only valid characters, if input cn, jp, kr characters, return format error
    else if (!/^[A-Za-z0-9!@#$%^&*()_+=\\[\]{}|;:,.<>?\\ -]+$/.test(pwd)) {
      return cb(PwdStrength.Fair, t('password_format_error'));
    }

    // At least three of the following four conditions must be met
    const conditions = [
      /[A-Z]/.test(pwd), // Contains uppercase letters
      /[a-z]/.test(pwd), // Contains lowercase letters
      /[0-9]/.test(pwd), // Contains numbers
      /[!@#$%^&*()_+=\\[\]{}|;:,.<>?\\-]/.test(pwd), // Contains special characters
    ];

    if (conditions.filter(Boolean).length < 3) return cb(PwdStrength.Weak, t('password_format_error'));

    // calculate strength using zxcvbn-ts
    const result = zxcvbn(pwd);
    if (result.score <= 1) return cb(PwdStrength.Weak, t('password_weak_error'));
    else if (result.score === 2) return cb(PwdStrength.Good);
    else if (result.score > 2) return cb(PwdStrength.Strong);
    return cb(PwdStrength.Fair, t('password_format_error'));
  };

  return { validatePwd, strength };
}
